import { oc } from "@orpc/contract";
import { z } from "zod";
import { ProductModule } from "@erp/shared-types";

const OrgSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
});

// A member's membership in one org. `isAdmin` is the per-org admin flag on
// um_users — distinct from the tenant-wide admin flag on um_tenant_users.
const MemberOrgSchema = OrgSummarySchema.extend({
  isAdmin: z.boolean(),
});

// ── tenant.get ──────────────────────────────────────────────

export const GetTenantOutputSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  organizationCount: z.number().int().nonnegative(),
  memberCount: z.number().int().nonnegative(),
});

export const getTenantContract = oc
  .output(GetTenantOutputSchema)
  .route({ method: "GET" });

// ── tenant.organizations.list ───────────────────────────────

export const ListTenantOrganizationsOutputSchema = z.object({
  organizations: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      memberCount: z.number().int().nonnegative(),
    }),
  ),
});

export const listTenantOrganizationsContract = oc
  .output(ListTenantOrganizationsOutputSchema)
  .route({ method: "GET" });

// ── tenant.organizations.create ─────────────────────────────

export const CreateTenantOrganizationInputSchema = z.object({
  name: z.string().trim().min(1).max(255),
  // Modules to enable on the new org. The admin chooses these via toggles in the
  // create dialog (pre-filled from the source org); independent of `copyFrom`.
  enabledModules: z.array(z.enum(ProductModule)).default([]),
  // When present, copy the named areas from an existing org in the same tenant.
  copyFrom: z
    .object({
      organizationId: z.string().uuid(),
      logo: z.boolean().default(false),
      azureEmail: z.boolean().default(false),
    })
    .optional(),
});

export const CreateTenantOrganizationOutputSchema = z.object({
  organization: OrgSummarySchema,
});

export const createTenantOrganizationContract = oc
  .input(CreateTenantOrganizationInputSchema)
  .output(CreateTenantOrganizationOutputSchema)
  .route({ method: "POST" });

// ── tenant.organizations.copyableConfig ─────────────────────

export const CopyableTenantOrganizationConfigInputSchema = z.object({
  organizationId: z.string().uuid(),
});

export const CopyableTenantOrganizationConfigOutputSchema = z.object({
  enabledModules: z.array(z.enum(ProductModule)),
  hasLogo: z.boolean(),
  azureConfigCount: z.number().int().nonnegative(),
});

export const copyableTenantOrganizationConfigContract = oc
  .input(CopyableTenantOrganizationConfigInputSchema)
  .output(CopyableTenantOrganizationConfigOutputSchema)
  .route({ method: "GET" });

// ── tenant.members.list ─────────────────────────────────────

export const ListTenantMembersOutputSchema = z.object({
  members: z.array(
    z.object({
      tenantUserId: z.string().uuid(),
      name: z.string(),
      isAdmin: z.boolean(),
      deactivatedAt: z.string().nullable(),
      primaryEmail: z.string().nullable(),
      organizations: z.array(MemberOrgSchema),
    }),
  ),
});

export const listTenantMembersContract = oc
  .output(ListTenantMembersOutputSchema)
  .route({ method: "GET" });

// ── tenant.members.add ──────────────────────────────────────

export const AddTenantMemberInputSchema = z.object({
  name: z.string().trim().min(1),
  email: z.string().email(),
  password: z.string().min(6),
  organizationIds: z.array(z.string().uuid()).min(1),
  isAdmin: z.boolean().optional(),
});

export const AddTenantMemberOutputSchema = z.object({
  tenantUserId: z.string().uuid(),
});

export const addTenantMemberContract = oc
  .input(AddTenantMemberInputSchema)
  .output(AddTenantMemberOutputSchema)
  .route({ method: "POST" });

// ── tenant.members.addToOrg ─────────────────────────────────

export const AddTenantMemberToOrgInputSchema = z.object({
  tenantUserId: z.string().uuid(),
  organizationId: z.string().uuid(),
});

export const AddTenantMemberToOrgOutputSchema = z.object({
  userId: z.string().uuid(),
});

export const addTenantMemberToOrgContract = oc
  .input(AddTenantMemberToOrgInputSchema)
  .output(AddTenantMemberToOrgOutputSchema)
  .route({ method: "POST" });

// ── tenant.members.removeFromOrg ────────────────────────────

export const RemoveTenantMemberFromOrgInputSchema = z.object({
  tenantUserId: z.string().uuid(),
  organizationId: z.string().uuid(),
});

export const RemoveTenantMemberFromOrgOutputSchema = z.object({
  success: z.literal(true),
});

export const removeTenantMemberFromOrgContract = oc
  .input(RemoveTenantMemberFromOrgInputSchema)
  .output(RemoveTenantMemberFromOrgOutputSchema)
  .route({ method: "POST" });

// ── tenant.members.setAdmin ─────────────────────────────────

export const SetTenantMemberAdminInputSchema = z.object({
  tenantUserId: z.string().uuid(),
  isAdmin: z.boolean(),
});

export const SetTenantMemberAdminOutputSchema = z.object({
  success: z.literal(true),
});

export const setTenantMemberAdminContract = oc
  .input(SetTenantMemberAdminInputSchema)
  .output(SetTenantMemberAdminOutputSchema)
  .route({ method: "POST" });

// ── tenant.members.setOrgAdmin ──────────────────────────────

export const SetTenantMemberOrgAdminInputSchema = z.object({
  tenantUserId: z.string().uuid(),
  organizationId: z.string().uuid(),
  isAdmin: z.boolean(),
});

export const SetTenantMemberOrgAdminOutputSchema = z.object({
  success: z.literal(true),
});

export const setTenantMemberOrgAdminContract = oc
  .input(SetTenantMemberOrgAdminInputSchema)
  .output(SetTenantMemberOrgAdminOutputSchema)
  .route({ method: "POST" });

// ── tenant.members.resetPassword ────────────────────────────

export const ResetTenantMemberPasswordInputSchema = z.object({
  tenantUserId: z.string().uuid(),
});

export const ResetTenantMemberPasswordOutputSchema = z.object({
  // Generated server-side and returned exactly once — it is never persisted in
  // plaintext, so there is no endpoint that can read it back.
  temporaryPassword: z.string(),
});

export const resetTenantMemberPasswordContract = oc
  .input(ResetTenantMemberPasswordInputSchema)
  .output(ResetTenantMemberPasswordOutputSchema)
  .route({ method: "POST" });

// ── Bundle ──────────────────────────────────────────────────

export const tenantContracts = {
  get: getTenantContract,
  organizations: {
    list: listTenantOrganizationsContract,
    create: createTenantOrganizationContract,
    copyableConfig: copyableTenantOrganizationConfigContract,
  },
  members: {
    list: listTenantMembersContract,
    add: addTenantMemberContract,
    addToOrg: addTenantMemberToOrgContract,
    removeFromOrg: removeTenantMemberFromOrgContract,
    setAdmin: setTenantMemberAdminContract,
    setOrgAdmin: setTenantMemberOrgAdminContract,
    resetPassword: resetTenantMemberPasswordContract,
  },
} as const;

export type CreateTenantOrganizationInput = z.infer<
  typeof CreateTenantOrganizationInputSchema
>;
export type CopyableTenantOrganizationConfigOutput = z.infer<
  typeof CopyableTenantOrganizationConfigOutputSchema
>;
export type AddTenantMemberInput = z.infer<typeof AddTenantMemberInputSchema>;
export type ListTenantMembersOutput = z.infer<
  typeof ListTenantMembersOutputSchema
>;
export type ListTenantOrganizationsOutput = z.infer<
  typeof ListTenantOrganizationsOutputSchema
>;
export type GetTenantOutput = z.infer<typeof GetTenantOutputSchema>;
